import { PickSampleExt } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { selectPicker } from "features/render";
import { getSurfaceProjectionTransform } from "features/render/svgUtils";
import { Picker } from "features/render/types";
import { hasMouseSupport } from "utils/misc";

import {
    CursorVisual,
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
    selectCursorActive,
    selectCursorPosition,
} from "./mobileMeasureSlice";

export interface MobileCursorCallbacks {
    picker: Picker;
    onPlace: (result: PickSampleExt, canvasPos: { x: number; y: number }) => void;
    onDragUpdate?: (result: PickSampleExt | null) => void;
    /** Called in persistent mode when tapping outside the D-pad area. */
    onTapElsewhere?: (canvasPos: { x: number; y: number }) => void;
    /** Called in persistent mode when D-pad is tapped without significant drag. */
    onDpadTap?: () => void;
    /** Called when D-pad is held without movement for 400ms (long-press). */
    onDpadLongPress?: () => void;
    enabled?: boolean;
    /** Picker radius for view.pick; use smaller value for edge-sensitive tools. */
    sampleDiscRadius?: number;
    /** Override hit validity when objectId === -1 can still be actionable. */
    isValidPick?: (result: PickSampleExt | null) => boolean;
    /**
     * In persistent mode, treat short D-pad tap as `onDpadTap` instead of placing.
     * Useful for PointLine mode transitions (arrow <-> transparent).
     */
    dpadTapCancelsPlace?: boolean;
    /**
     * When true, the cursor persists after placement:
     * - First tap places the cursor (no measurement).
     * - Drag from D-pad area moves cursor; release creates measurement.
     * - Cursor stays visible between measurements.
     */
    persistent?: boolean;
}

const DPAD_HIT_RADIUS = 36;
const DPAD_DRAG_THRESHOLD_SQ = 8 * 8;
const DPAD_LONG_PRESS_MS = 400;

export function useMobileMeasureCursor({
    picker,
    onPlace,
    onDragUpdate,
    onTapElsewhere,
    onDpadTap,
    onDpadLongPress,
    enabled = true,
    sampleDiscRadius = 8,
    isValidPick,
    dpadTapCancelsPlace = false,
    persistent = false,
}: MobileCursorCallbacks) {
    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const {
        state: { view, canvas, size },
    } = useExplorerGlobals();

    const currentPicker = useAppSelector(selectPicker);
    const cursorActive = useAppSelector(selectCursorActive);
    const cursorPosition = useAppSelector(selectCursorPosition);

    const lastPickRef = useRef<PickSampleExt | null>(null);
    const lastCanvasPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const pickingRef = useRef(false);
    const touchActiveRef = useRef(false);
    const cursorPlacedRef = useRef(false);
    const draggingRef = useRef(false);
    const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
    const dragMovedRef = useRef(false);
    const dpadLongPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dpadLongPressFiredRef = useRef(false);
    const wasActiveRef = useRef(false);

    const isActive = enabled && !hasMouseSupport() && currentPicker === picker;

    const isPickValid = useCallback(
        (result: PickSampleExt | null) => {
            if (!result) {
                return false;
            }
            if (isValidPick) {
                return isValidPick(result);
            }
            return result.objectId !== -1;
        },
        [isValidPick],
    );

    const determineCursorVisual = useCallback(
        (result: PickSampleExt | null): CursorVisual => {
            if (!isPickValid(result)) {
                return "noHit";
            }

            // Match desktop semantics for Dimension hover:
            // - edge -> 2-sided cursor
            // - surface/corner -> 4-sided cursor
            if (picker === Picker.Dimension && result?.sampleType === "edge") {
                return "pointSnap";
            }

            return result?.sampleType === "surface" ? "surfaceSnap" : "pointSnap";
        },
        [isPickValid, picker],
    );

    const doPick = useCallback(
        async (x: number, y: number) => {
            if (!view || pickingRef.current) {
                return;
            }
            pickingRef.current = true;
            try {
                const result = await view.pick(x, y, {
                    sampleDiscRadius,
                    async: false,
                });
                lastPickRef.current = result ?? null;
                dispatchLastPickSample(lastPickSampleActions.set(result ?? null));

                if (!onDragUpdate) {
                    const visual = determineCursorVisual(result ?? null);
                    dispatch(mobileMeasureActions.setCursorVisual(visual));
                }

                if (isPickValid(result ?? null) && result) {
                    const screenPos = view.convert.worldSpaceToScreenSpace([result.position]);
                    if (screenPos?.[0]) {
                        dispatch(mobileMeasureActions.setIndicatorPosition({ x: screenPos[0][0], y: screenPos[0][1] }));
                        const transform =
                            result.sampleType === "surface"
                                ? getSurfaceProjectionTransform(
                                      result.normalVS as [number, number, number],
                                      screenPos[0][0],
                                      screenPos[0][1],
                                      size,
                                      view,
                                  )
                                : null;
                        dispatch(mobileMeasureActions.setIndicatorTransform(transform));
                    }
                } else {
                    dispatch(mobileMeasureActions.setIndicatorPosition(null));
                    dispatch(mobileMeasureActions.setIndicatorTransform(null));
                }

                onDragUpdate?.(result ?? null);
            } finally {
                pickingRef.current = false;
            }
        },
        [
            view,
            sampleDiscRadius,
            size,
            dispatch,
            dispatchLastPickSample,
            determineCursorVisual,
            onDragUpdate,
            isPickValid,
        ],
    );

    useEffect(() => {
        if (!isActive || !canvas) {
            return;
        }

        const getCanvasOffset = (touch: Touch) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: touch.clientX - rect.left,
                y: touch.clientY - rect.top,
            };
        };

        const getMouseCanvasOffset = (e: globalThis.MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            return {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        };

        const isNearDpad = (pos: { x: number; y: number }) => {
            const dpadCenter = lastCanvasPosRef.current;
            const dpadDx = pos.x - dpadCenter.x;
            const dpadDy = pos.y - dpadCenter.y;
            const hitRadiusSq = DPAD_HIT_RADIUS * DPAD_HIT_RADIUS;
            return dpadDx * dpadDx + dpadDy * dpadDy <= hitRadiusSq;
        };

        if (persistent) {
            // ── PERSISTENT MODE ──
            // Tap = place cursor. Drag D-pad = move + release = measure. Cursor stays.

            const pickTarget = (pos: { x: number; y: number }) => ({
                x: pos.x + PICK_TARGET_OFFSET.x,
                y: pos.y + PICK_TARGET_OFFSET.y,
            });

            const placeCursor = (pos: { x: number; y: number }) => {
                cursorPlacedRef.current = true;
                lastPickRef.current = null;
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorActive(true));
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const startDrag = (pos: { x: number; y: number }) => {
                draggingRef.current = true;
                dragStartPosRef.current = { ...pos };
                dragMovedRef.current = false;
                lastPickRef.current = null;
                dpadLongPressFiredRef.current = false;
                dispatch(mobileMeasureActions.setIsDragging(true));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
                if (onDpadLongPress) {
                    if (dpadLongPressTimerRef.current) {
                        clearTimeout(dpadLongPressTimerRef.current);
                    }
                    dpadLongPressTimerRef.current = setTimeout(() => {
                        dpadLongPressTimerRef.current = null;
                        dpadLongPressFiredRef.current = true;
                        onDpadLongPress();
                    }, DPAD_LONG_PRESS_MS);
                }
            };

            const moveDrag = (pos: { x: number; y: number }) => {
                const dragStartPos = dragStartPosRef.current;
                if (dragStartPos && !dragMovedRef.current) {
                    const dx = pos.x - dragStartPos.x;
                    const dy = pos.y - dragStartPos.y;
                    if (dx * dx + dy * dy >= DPAD_DRAG_THRESHOLD_SQ) {
                        dragMovedRef.current = true;
                        if (dpadLongPressTimerRef.current) {
                            clearTimeout(dpadLongPressTimerRef.current);
                            dpadLongPressTimerRef.current = null;
                        }
                    }
                }
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const endDrag = () => {
                if (!draggingRef.current) {
                    return;
                }
                if (dpadLongPressTimerRef.current) {
                    clearTimeout(dpadLongPressTimerRef.current);
                    dpadLongPressTimerRef.current = null;
                }
                draggingRef.current = false;
                dispatch(mobileMeasureActions.setIsDragging(false));
                const wasShortTap = !dragMovedRef.current;
                dragStartPosRef.current = null;
                if (dpadLongPressFiredRef.current && wasShortTap) {
                    return;
                }
                if (wasShortTap && dpadTapCancelsPlace && onDpadTap) {
                    onDpadTap();
                    return;
                }
                if (lastPickRef.current) {
                    onPlace(lastPickRef.current, pickTarget(lastCanvasPosRef.current));
                } else {
                    onDpadTap?.();
                }
            };

            // Document-level capture-phase handlers give the DPAD priority over
            // non-interactive overlay elements (gizmo canvas, SVG layers, labels)
            // while still letting interactive UI (buttons) handle their own events.
            const isInCanvasArea = (clientX: number, clientY: number) => {
                const rect = canvas.getBoundingClientRect();
                return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
            };

            const isInteractiveOverlay = (target: EventTarget | null) =>
                (target as HTMLElement)?.closest?.("button, [role='button']") !== null;

            const handleTouchStart = (e: TouchEvent) => {
                if (e.touches.length !== 1) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const touch = e.touches[0];
                const pos = getCanvasOffset(touch);

                if (cursorPlacedRef.current && isNearDpad(pos)) {
                    touchActiveRef.current = true;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    startDrag(pos);
                } else if (!cursorPlacedRef.current && isInCanvasArea(touch.clientX, touch.clientY)) {
                    touchActiveRef.current = true;
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    placeCursor(pos);
                    onTapElsewhere?.(pos);
                } else if (cursorPlacedRef.current && isInCanvasArea(touch.clientX, touch.clientY)) {
                    // Canvas-area tap away from DPAD: run tap-elsewhere logic
                    // (e.g. select nearby laser) but let the event pass through
                    // so camera orbit still works.
                    onTapElsewhere?.(pos);
                }
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (!touchActiveRef.current || e.touches.length !== 1) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                if (draggingRef.current) {
                    moveDrag(getCanvasOffset(e.touches[0]));
                }
            };

            const handleTouchEnd = (e: TouchEvent) => {
                if (touchActiveRef.current) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    touchActiveRef.current = false;
                }
                endDrag();
            };

            // Pointer handlers mirror touch logic for mouse/pen input (forceMobile).
            // Touch-originated pointer events are skipped (touch handlers take priority).
            const handlePointerDown = (e: PointerEvent) => {
                if (e.pointerType === "touch" || e.button !== 0) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const pos = getMouseCanvasOffset(e);
                if (cursorPlacedRef.current && isNearDpad(pos)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    startDrag(pos);
                } else if (!cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    placeCursor(pos);
                    onTapElsewhere?.(pos);
                } else if (cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    onTapElsewhere?.(pos);
                }
            };

            const handlePointerMove = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !draggingRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveDrag(getMouseCanvasOffset(e));
            };

            const handlePointerUp = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !draggingRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                endDrag();
            };

            // Mouse handlers intercept engine listeners (bubble-phase).
            const handleMouseDown = (e: MouseEvent) => {
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const pos = getMouseCanvasOffset(e);
                if (cursorPlacedRef.current && isNearDpad(pos)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                } else if (!cursorPlacedRef.current && isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (draggingRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseUp = (e: MouseEvent) => {
                if (draggingRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            document.addEventListener("touchstart", handleTouchStart, { capture: true, passive: false });
            document.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
            document.addEventListener("touchend", handleTouchEnd, { capture: true, passive: false });
            document.addEventListener("touchcancel", handleTouchEnd, { capture: true, passive: false });
            document.addEventListener("pointerdown", handlePointerDown, true);
            document.addEventListener("pointermove", handlePointerMove, true);
            document.addEventListener("pointerup", handlePointerUp, true);
            document.addEventListener("mousedown", handleMouseDown, true);
            document.addEventListener("mousemove", handleMouseMove, true);
            document.addEventListener("mouseup", handleMouseUp, true);

            return () => {
                document.removeEventListener("touchstart", handleTouchStart, true);
                document.removeEventListener("touchmove", handleTouchMove, true);
                document.removeEventListener("touchend", handleTouchEnd, true);
                document.removeEventListener("touchcancel", handleTouchEnd, true);
                document.removeEventListener("pointerdown", handlePointerDown, true);
                document.removeEventListener("pointermove", handlePointerMove, true);
                document.removeEventListener("pointerup", handlePointerUp, true);
                document.removeEventListener("mousedown", handleMouseDown, true);
                document.removeEventListener("mousemove", handleMouseMove, true);
                document.removeEventListener("mouseup", handleMouseUp, true);
            };
        } else {
            // ── NON-PERSISTENT MODE (laser, coordinate, point-line) ──
            // Press → drag → release = place measurement. Cursor disappears after.

            const pickTarget = (pos: { x: number; y: number }) => ({
                x: pos.x + PICK_TARGET_OFFSET.x,
                y: pos.y + PICK_TARGET_OFFSET.y,
            });

            const activateAtPosition = (pos: { x: number; y: number }) => {
                touchActiveRef.current = true;
                lastPickRef.current = null;
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorActive(true));
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                dispatch(mobileMeasureActions.setIsDragging(true));
                dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const moveToPosition = (pos: { x: number; y: number }) => {
                lastCanvasPosRef.current = pos;
                dispatch(mobileMeasureActions.setCursorPosition(pos));
                const pt = pickTarget(pos);
                doPick(pt.x, pt.y);
            };

            const deactivate = () => {
                if (!touchActiveRef.current) {
                    return;
                }
                touchActiveRef.current = false;
                dispatch(mobileMeasureActions.setIsDragging(false));
                if (lastPickRef.current) {
                    onPlace(lastPickRef.current, pickTarget(lastCanvasPosRef.current));
                }
                dispatch(mobileMeasureActions.setCursorActive(false));
            };

            const isInCanvasArea = (clientX: number, clientY: number) => {
                const rect = canvas.getBoundingClientRect();
                return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
            };

            const isInteractiveOverlay = (target: EventTarget | null) =>
                (target as HTMLElement)?.closest?.("button, [role='button']") !== null;

            const handleTouchStart = (e: TouchEvent) => {
                if (e.touches.length !== 1) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                const touch = e.touches[0];
                if (!isInCanvasArea(touch.clientX, touch.clientY)) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                activateAtPosition(getCanvasOffset(touch));
            };

            const handleTouchMove = (e: TouchEvent) => {
                if (!touchActiveRef.current || e.touches.length !== 1) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveToPosition(getCanvasOffset(e.touches[0]));
            };

            const handleDeactivate = (e: TouchEvent) => {
                if (touchActiveRef.current) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
                deactivate();
            };

            // Pointer handlers mirror touch logic for mouse/pen input (forceMobile).
            const handlePointerDown = (e: PointerEvent) => {
                if (e.pointerType === "touch" || e.button !== 0) {
                    return;
                }
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                if (isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    activateAtPosition(getMouseCanvasOffset(e));
                }
            };

            const handlePointerMove = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !touchActiveRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                moveToPosition(getMouseCanvasOffset(e));
            };

            const handlePointerUp = (e: PointerEvent) => {
                if (e.pointerType === "touch" || !touchActiveRef.current) {
                    return;
                }
                e.preventDefault();
                e.stopImmediatePropagation();
                deactivate();
            };

            // Mouse handlers intercept engine listeners (bubble-phase).
            const handleMouseDown = (e: MouseEvent) => {
                if (isInteractiveOverlay(e.target)) {
                    return;
                }
                if (isInCanvasArea(e.clientX, e.clientY)) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseMove = (e: MouseEvent) => {
                if (touchActiveRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            const handleMouseUp = (e: MouseEvent) => {
                if (touchActiveRef.current) {
                    e.stopImmediatePropagation();
                }
            };

            document.addEventListener("touchstart", handleTouchStart, { capture: true, passive: false });
            document.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
            document.addEventListener("touchend", handleDeactivate, { capture: true, passive: false });
            document.addEventListener("touchcancel", handleDeactivate, { capture: true, passive: false });
            document.addEventListener("pointerdown", handlePointerDown, true);
            document.addEventListener("pointermove", handlePointerMove, true);
            document.addEventListener("pointerup", handlePointerUp, true);
            document.addEventListener("mousedown", handleMouseDown, true);
            document.addEventListener("mousemove", handleMouseMove, true);
            document.addEventListener("mouseup", handleMouseUp, true);

            return () => {
                document.removeEventListener("touchstart", handleTouchStart, true);
                document.removeEventListener("touchmove", handleTouchMove, true);
                document.removeEventListener("touchend", handleDeactivate, true);
                document.removeEventListener("touchcancel", handleDeactivate, true);
                document.removeEventListener("pointerdown", handlePointerDown, true);
                document.removeEventListener("pointermove", handlePointerMove, true);
                document.removeEventListener("pointerup", handlePointerUp, true);
                document.removeEventListener("mousedown", handleMouseDown, true);
                document.removeEventListener("mousemove", handleMouseMove, true);
                document.removeEventListener("mouseup", handleMouseUp, true);
                touchActiveRef.current = false;
            };
        }
    }, [
        isActive,
        canvas,
        dispatch,
        doPick,
        onPlace,
        onTapElsewhere,
        onDpadTap,
        onDpadLongPress,
        persistent,
        dpadTapCancelsPlace,
    ]);

    // Sync cursorPlacedRef when cursor is activated from outside (e.g. teleport via selectPointAsRoot)
    useEffect(() => {
        if (isActive && persistent && cursorActive && cursorPosition) {
            cursorPlacedRef.current = true;
            lastCanvasPosRef.current = cursorPosition;
        } else if (isActive && persistent && !cursorActive) {
            // Keep refs aligned with store reset / tool switches.
            cursorPlacedRef.current = false;
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
        }
    }, [isActive, persistent, cursorActive, cursorPosition]);

    // Expose forceStartDrag synchronously via module-level ref so SVG long-press
    // can initiate a drag immediately (no async React effect timing issues).
    mobileMeasureCursorRef.forceStartDrag = (pos: { x: number; y: number }) => {
        cursorPlacedRef.current = true;
        draggingRef.current = true;
        dragStartPosRef.current = { ...pos };
        dragMovedRef.current = false;
        lastCanvasPosRef.current = pos;
        lastPickRef.current = null;
        dispatch(mobileMeasureActions.setIsDragging(true));
    };

    useEffect(() => {
        if (isActive && !wasActiveRef.current) {
            // Entering this picker: always start from a clean "tap to place D-pad" state.
            cursorPlacedRef.current = false;
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
            dispatch(mobileMeasureActions.reset());
            dispatchLastPickSample(lastPickSampleActions.set(null));
        } else if (!isActive && wasActiveRef.current) {
            // Leaving this picker: fully clear overlay and internal interaction refs.
            cursorPlacedRef.current = false;
            draggingRef.current = false;
            touchActiveRef.current = false;
            dragStartPosRef.current = null;
            dragMovedRef.current = false;
            lastPickRef.current = null;
            dispatch(mobileMeasureActions.reset());
            dispatchLastPickSample(lastPickSampleActions.set(null));
        }
        wasActiveRef.current = isActive;
    }, [isActive, dispatch, dispatchLastPickSample]);

    useEffect(() => {
        return () => {
            dispatch(mobileMeasureActions.reset());
            mobileMeasureCursorRef.forceStartDrag = null;
        };
    }, [dispatch]);

    return { cursorActive, lastPickResult: lastPickRef.current };
}
